import { api } from './api';
import humps from 'humps';
import { unwrapList } from './unwrapList';
import { SubscriptionPlan, CurrentSubscription } from '../types/plan';

export interface CreditPackage {
    id: string;
    name: string;
    credits: number;
    price: number;
    imageCount: number;
    pricePerImage: number;
    discount?: string;
    isPopular?: boolean;
}

export interface BillingTransaction {
    id: string;
    date: string;
    description: string;
    amount: number;
    status: 'paid' | 'pending' | 'failed';
}

// Admin Billing Types
export interface CustomerSummary {
    id: number;
    name: string;
    email: string;
    role: string;
    isActive: boolean;
    creditsBalance: number;
    totalPurchased: number;
    totalConsumed: number;
    campaignsCount: number;
    imagesCount: number;
    totalRequested?: number;
    totalGenerated?: number;
    overGenerated?: boolean;
    videosCount: number;
    avgCostPerImage: number;
    lastActivity: string | null;
    createdAt: string;
}

export interface ImageGenerationReport {
    summary: { totalRequested: number; totalGenerated: number; difference: number; overGenerated: boolean };
    fallback: { count: number; message: string };
    alerts: { tasksOverGenerated: Array<{ taskId: number; requested: number; generated: number; createdAt: string }>; hasAnomalyFallbackAfterSuccess: boolean };
    suggestedReply: string;
    byProvider: Record<string, number>;
    bySource: Record<string, number>;
}

export interface ImageGenerationTaskRow {
    taskId: number;
    campaignId?: number;
    campaignName?: string;
    lookIndex?: number | null;
    poseIndex?: number | null;
    userId: number;
    userName?: string;
    userEmail?: string;
    requested: number;
    generated: number;
    editedCount: number;
    prompt: string;
    promptFull: string;
    credits: number;
    resolution: string;
    /** Stable slug of the profile the user picked; null when they kept the default. */
    setup: string | null;
    /** Display name of that profile — falls back to the slug if it was deleted. */
    setupLabel: string | null;
    fallback: boolean;
    provider: string;
    model: string | null;
    source: string;
    imageUrls?: string[];
    createdAt: string;
}

export interface VideoGenerationTaskRow {
    taskId: number;
    campaignId?: number;
    campaignName?: string;
    userId: number;
    userName?: string;
    userEmail?: string;
    prompt: string;
    promptFull: string;
    aspectRatio: string;
    resolution: string;
    durationSeconds: number;
    mode: string;
    generated: number;
    credits: number;
    provider: string | null;
    providers: string[];
    fallback: boolean;
    videoUrls?: string[];
    createdAt: string;
}

export interface CustomerUsage {
    user: {
        id: number;
        name: string;
        email: string;
        createdAt: string;
    };
    balance: {
        currentBalance: number;
        nextExpirationDate: string | null;
        creditsExpiring: number | null;
    };
    totals: {
        totalPurchased: number;
        totalConsumed: number;
        totalCampaigns: number;
        totalImages: number;
        totalVideos: number;
    };
    usageByAction: Record<string, { count: number; totalCredits: number }>;
    usageByCampaign: Array<{
        id: number;
        name: string;
        status: string;
        creditsConsumed: number;
        creditsEstimated: number;
        tasksCount: number;
        imagesCount: number;
        createdAt: string;
    }>;
    monthlyUsage: Array<{
        month: string;
        totalConsumed: number;
        transactionCount: number;
    }>;
}

export interface RevenueReport {
    monthly: Array<{
        month: string;
        totalPurchased: number;
        totalConsumed: number;
        uniqueCustomers: number;
        transactionCount: number;
    }>;
    totals: {
        totalPurchased: number;
        totalConsumed: number;
        totalCustomers: number;
        activeCustomers: number;
    };
    period: {
        startDate: string;
        endDate: string;
    };
}

export interface UsageReport {
    usageByAction: Array<{
        actionType: string;
        count: number;
        totalCredits: number;
        avgCredits: number;
    }>;
    topConsumers: Array<{
        userId: number;
        userName: string;
        userEmail: string;
        totalConsumed: number;
        transactionCount: number;
    }>;
    dailyUsage: Array<{
        date: string;
        totalConsumed: number;
        transactionCount: number;
    }>;
    period: {
        startDate: string;
        endDate: string;
    };
}

// User Credit Usage Types
export interface UsageSummary {
    currentBalance: number;
    totalPurchased: number;
    totalConsumed: number;
    totalExpired: number;
    currentMonthConsumed: number;
    imagesGenerated: number;
    videosGenerated: number;
    campaignsCount: number;
    avgCostPerImage: number;
    avgCostPerVideo: number;
}

export interface CreditLedgerEntry {
    id: string;
    creditsGranted: number;
    creditsRemaining: number;
    creditsUsed: number;
    purchaseDate: string;
    expiresAt: string | null;
    sourceDescription: string | null;
    isPromotional: boolean;
    isExpired: boolean;
    isExpiringSoon: boolean;
    daysUntilExpiration: number | null;
}

export interface PackagesResponse {
    packages: CreditPackage[];
    canPurchase: boolean;
}

export const billingService = {
    getPackages: async (): Promise<PackagesResponse> => {
        const response = await api.get('/credits/bundles');
        const data = response.data as any;
        const bundles = data.bundles || data;
        const canPurchase = data.can_purchase ?? data.canPurchase ?? true;

        return {
            packages: (Array.isArray(bundles) ? bundles : []).map((bundle: any) => ({
                id: bundle.id,
                name: bundle.name,
                credits: bundle.credits,
                price: parseFloat(bundle.price),
                imageCount: bundle.credits,
                pricePerImage: bundle.credits > 0 ? parseFloat(bundle.price) / bundle.credits : 0,
                discount: bundle.discountInfo ?? bundle.discount_info,
                isPopular: bundle.isPromotional ?? bundle.is_promotional
            })),
            canPurchase,
        };
    },

    getHistory: async (filters?: {
        page?: number;
        perPage?: number;
        startDate?: string;
        endDate?: string;
        status?: string;
    }): Promise<{ data: BillingTransaction[]; meta?: { lastPage: number; total: number } }> => {
        const params: Record<string, any> = {};
        if (filters?.page) params.page = filters.page;
        if (filters?.perPage) params.per_page = filters.perPage;
        if (filters?.startDate) params.start_date = filters.startDate;
        if (filters?.endDate) params.end_date = filters.endDate;
        if (filters?.status) params.status = filters.status;

        const response = await api.get('/credits/transactions', { params });
        const responseData = response.data as any;
        const transactions = responseData.data || responseData;

        const mappedTransactions = (Array.isArray(transactions) ? transactions : []).map((tx: any) => ({
            id: tx.id,
            date: tx.transactionDate || tx.transaction_date || tx.createdAt || tx.created_at,
            description: tx.description || 'Transação de créditos',
            amount: parseFloat(tx.amount || 0),
            status: tx.type || tx.status || 'paid'
        }));

        return {
            data: mappedTransactions,
            meta: responseData.meta ? {
                lastPage: responseData.meta.last_page || responseData.meta.lastPage || 1,
                total: responseData.meta.total || mappedTransactions.length,
            } : undefined,
        };
    },

    purchasePackage: async (packageId: string): Promise<string> => {
        const response = await api.post(`/credits/purchase/${packageId}`);
        const data = response.data as { checkoutUrl: string };
        return data.checkoutUrl;
    },

    verifyPayment: async (sessionId: string): Promise<boolean> => {
        const response = await api.get(`/credits/verify-payment/${sessionId}`);
        return (response.data as { verified: boolean }).verified;
    },

    getBalance: async (): Promise<number> => {
        const response = await api.get('/credits/balance');
        return (response.data as { currentBalance: number }).currentBalance;
    },

    // User Credit Usage Methods
    getUsageSummary: async (): Promise<UsageSummary> => {
        const response = await api.get('/credits/usage/summary');
        return humps.camelizeKeys(response.data.data) as UsageSummary;
    },

    getUsageByAction: async (): Promise<Record<string, { count: number; totalCredits: number; avgCredits: number }>> => {
        const response = await api.get('/credits/usage/by-action');
        return humps.camelizeKeys(response.data.data) as Record<string, { count: number; totalCredits: number; avgCredits: number }>;
    },

    getUsageByCampaign: async (limit: number = 10): Promise<any[]> => {
        const response = await api.get('/credits/usage/by-campaign', { params: { limit } });
        return humps.camelizeKeys(unwrapList(response.data)) as any[];
    },

    getMonthlyUsage: async (months: number = 6): Promise<any[]> => {
        const response = await api.get('/credits/usage/monthly', { params: { months } });
        return humps.camelizeKeys(unwrapList(response.data)) as any[];
    },

    getCreditLedger: async (): Promise<CreditLedgerEntry[]> => {
        const response = await api.get('/credits/ledger');
        return humps.camelizeKeys(unwrapList(response.data)) as CreditLedgerEntry[];
    },

    estimateActionCost: async (actionType: string, params?: { quantity?: number; resolution?: string; duration?: number }): Promise<{ actionType: string; estimatedCost: number; breakdown: any }> => {
        const response = await api.post('/credits/estimate-action', { action_type: actionType, ...params });
        return humps.camelizeKeys(response.data.data) as { actionType: string; estimatedCost: number; breakdown: any };
    },
};

// Admin Billing Service
export const adminBillingService = {
    getCustomersSummary: async (params?: {
        page?: number;
        perPage?: number;
        sortBy?: string;
        sortOrder?: 'asc' | 'desc';
        search?: string;
    }): Promise<{ data: CustomerSummary[]; meta: { currentPage: number; lastPage: number; perPage: number; total: number } }> => {
        const response = await api.get('/admin/billing/customers/summary', { params: humps.decamelizeKeys(params) });
        return {
            data: humps.camelizeKeys(response.data.data) as CustomerSummary[],
            meta: humps.camelizeKeys(response.data.meta) as { currentPage: number; lastPage: number; perPage: number; total: number }
        };
    },

    getCustomerUsage: async (userId: number): Promise<CustomerUsage> => {
        const response = await api.get(`/admin/billing/customers/${userId}/usage`);
        return humps.camelizeKeys(response.data.data) as CustomerUsage;
    },

    getCustomerTransactions: async (userId: number, params?: { page?: number; perPage?: number; type?: string; startDate?: string; endDate?: string }): Promise<{ data: any[]; meta: any }> => {
        const response = await api.get(`/admin/billing/customers/${userId}/transactions`, { params: humps.decamelizeKeys(params) });
        return {
            data: humps.camelizeKeys(response.data.data) as any[],
            meta: humps.camelizeKeys(response.data.meta) as any
        };
    },

    getRevenueReport: async (params?: { startDate?: string; endDate?: string }): Promise<RevenueReport> => {
        const response = await api.get('/admin/billing/reports/revenue', { params: humps.decamelizeKeys(params) });
        return humps.camelizeKeys(response.data.data) as RevenueReport;
    },

    getUsageReport: async (params?: { startDate?: string; endDate?: string }): Promise<UsageReport> => {
        const response = await api.get('/admin/billing/reports/usage', { params: humps.decamelizeKeys(params) });
        return humps.camelizeKeys(response.data.data) as UsageReport;
    },

    exportCsv: async (): Promise<Blob> => {
        const response = await api.get('/admin/billing/export/csv', { responseType: 'blob' });
        return response.data as Blob;
    },

    getImageGenerationReport: async (userId: number): Promise<ImageGenerationReport> => {
        const response = await api.get(`/admin/billing/customers/${userId}/image-generation-report`);
        return humps.camelizeKeys(response.data.data) as ImageGenerationReport;
    },

    getImageGenerationTasks: async (params?: {
        userId?: number;
        email?: string;
        dateFrom?: string;
        dateTo?: string;
        creditsMin?: number;
        resolution?: string;
        fallback?: boolean | '';
        generatedMin?: number;
        page?: number;
        perPage?: number;
    }): Promise<{ data: ImageGenerationTaskRow[]; meta: { currentPage: number; lastPage: number; perPage: number; total: number } }> => {
        const response = await api.get('/admin/billing/image-generation-tasks', { params: humps.decamelizeKeys(params) });
        return {
            data: (response.data.data || []).map((row: any) => humps.camelizeKeys(row)) as ImageGenerationTaskRow[],
            meta: humps.camelizeKeys(response.data.meta) as { currentPage: number; lastPage: number; perPage: number; total: number }
        };
    },

    getCustomerImageGenerationTasks: async (userId: number, params?: { page?: number; perPage?: number; dateFrom?: string; dateTo?: string }): Promise<{ data: ImageGenerationTaskRow[]; meta: { currentPage: number; lastPage: number; perPage: number; total: number } }> => {
        const response = await api.get(`/admin/billing/customers/${userId}/image-generation-tasks`, { params: humps.decamelizeKeys(params) });
        return {
            data: (response.data.data || []).map((row: any) => humps.camelizeKeys(row)) as ImageGenerationTaskRow[],
            meta: humps.camelizeKeys(response.data.meta) as { currentPage: number; lastPage: number; perPage: number; total: number }
        };
    },

    getVideoGenerationTasks: async (params?: {
        userId?: number;
        email?: string;
        dateFrom?: string;
        dateTo?: string;
        creditsMin?: number;
        resolution?: string;
        provider?: string;
        mode?: string;
        generatedMin?: number;
        page?: number;
        perPage?: number;
    }): Promise<{ data: VideoGenerationTaskRow[]; meta: { currentPage: number; lastPage: number; perPage: number; total: number } }> => {
        const response = await api.get('/admin/billing/video-generation-tasks', { params: humps.decamelizeKeys(params) });
        return {
            data: (response.data.data || []).map((row: any) => humps.camelizeKeys(row)) as VideoGenerationTaskRow[],
            meta: humps.camelizeKeys(response.data.meta) as { currentPage: number; lastPage: number; perPage: number; total: number }
        };
    },

    getCustomerVideoGenerationTasks: async (userId: number, params?: { page?: number; perPage?: number; dateFrom?: string; dateTo?: string }): Promise<{ data: VideoGenerationTaskRow[]; meta: { currentPage: number; lastPage: number; perPage: number; total: number } }> => {
        const response = await api.get(`/admin/billing/customers/${userId}/video-generation-tasks`, { params: humps.decamelizeKeys(params) });
        return {
            data: (response.data.data || []).map((row: any) => humps.camelizeKeys(row)) as VideoGenerationTaskRow[],
            meta: humps.camelizeKeys(response.data.meta) as { currentPage: number; lastPage: number; perPage: number; total: number }
        };
    },
};

// Subscription Service
export const subscriptionService = {
    getPlans: async (): Promise<SubscriptionPlan[]> => {
        const response = await api.get('/subscriptions/plans');
        return unwrapList<any>(response.data).map((plan: any) => humps.camelizeKeys(plan) as SubscriptionPlan);
    },

    getCurrentSubscription: async (): Promise<CurrentSubscription> => {
        const response = await api.get('/subscriptions/current');
        return humps.camelizeKeys(response.data) as CurrentSubscription;
    },

    subscribe: async (planId: string): Promise<string> => {
        const response = await api.post(`/subscriptions/subscribe/${planId}`);
        const data = response.data as { checkoutUrl: string };
        return data.checkoutUrl;
    },

    cancelSubscription: async (): Promise<{ message: string; endsAt: string; daysRemaining: number }> => {
        const response = await api.post('/subscriptions/cancel');
        return humps.camelizeKeys(response.data) as { message: string; endsAt: string; daysRemaining: number };
    },
};

// Backward compat — keep class-style exports as aliases
export const BillingService = billingService;
export const AdminBillingService = adminBillingService;
export const SubscriptionService = subscriptionService;
